// src/utils/levelConfig.js

const MEMORY_NAMES = [
  'Color Cascade', 'Prism Paths', 'Crystal Quest', 'Spectrum Shift',
  'Glow Grid', 'Prism Master', 'Hue Havoc', 'Light Labyrinth',
  'Chroma Storm', 'Radiant Ruins', 'Spectral Vault', 'Neon Nexus',
  'Pixel Prism', 'Glass Garden', 'Aurora Array', 'Photon Field',
  'Mirage Matrix', 'Lumin Lock', 'Kaleido Core', 'Flux Fortress',
  'Glimmer Grid', 'Echo Spectrum', 'Beacon Burst', 'Solar Sequence',
  'Quantum Quilt',
];

const RIDDLE_NAMES = [
  'Riddle Pals', 'Neon Maze', 'Cipher Circuit', 'Puzzle Pulse',
  'Logic Lantern', 'Enigma Engine', 'Mind Maze', 'Riddle Reactor',
  'Cryptic Core', 'Puzzle Portal', 'Brain Beacon', 'Riddle Relay',
  'Logic Loop', 'Enigma Echo', 'Puzzle Prism', 'Mind Mirror',
  'Cipher Storm', 'Riddle Rift', 'Logic Labyrinth', 'Final Enigma',
  'Cryptic Comet', 'Puzzle Paradox', 'Mind Maelstrom', 'Riddle Reckoning',
  'Ultimate Enigma',
];

/**
 * Memory Grid difficulty progression (odd levels: 1, 3, 5, 7 …)
 *
 * rung 0 = Level  1  →  6 colors, 15 moves, 5s preview
 * rung 1 = Level  3  →  6 colors, 13 moves, 5s preview
 * rung 2 = Level  5  →  7 colors, 11 moves, 4s preview
 * rung 3 = Level  7  →  7 colors,  9 moves, 4s preview
 * rung 4 = Level  9  →  8 colors,  7 moves, 3s preview
 * rung 5 = Level 11  →  8 colors,  6 moves, 3s preview (floor)
 * rung 6 = Level 13  →  9 colors,  6 moves, 3s preview
 */
function getMemoryDifficulty(level) {
  const rung = Math.floor((level - 1) / 2);

  const colors         = Math.min(6 + Math.floor(rung / 2), 9);
  const moves          = Math.max(15 - rung * 2, 6);
  const previewSeconds = Math.max(5 - Math.floor(rung / 4), 2);
  const distractions   = rung >= 6;
  const glitchEffect   = rung >= 10;
  const boardFilled    = rung >= 9;

  return { colors, moves, previewSeconds, distractions, glitchEffect, boardFilled };
}

/**
 * Riddle difficulty progression (even levels: 2, 4, 6, 8 …)
 *
 * rung 0 = Level 2  → 5 characters, 6 clues shown,  0 red herrings
 * rung 1 = Level 4  → 5 characters, 5 clues shown,  0 red herrings
 * rung 2 = Level 6  → 6 characters, 5 clues shown,  0 red herrings
 * rung 3 = Level 8  → 6 characters, 4 clues shown,  0 red herrings
 * rung 4 = Level 10 → 7 characters, 4 clues shown,  1 red herring
 * rung 5 = Level 12 → 7 characters, 3 clues shown,  1 red herring
 * rung 6 = Level 14 → 8 characters, 3 clues shown,  2 red herrings
 * rung 7 = Level 16 → 8 characters, 2 clues shown,  2 red herrings
 *
 * characterCount drives which SCENARIO is loaded (5–8 characters).
 * clueCount drives how many clues from that scenario are visible.
 * redHerrings drives how many misleading clues are appended.
 */
function getRiddleDifficulty(level) {
  const rung = Math.floor((level - 2) / 2);

  // Characters: 5 for rungs 0-1, 6 for rungs 2-3, 7 for rungs 4-5, 8 for rungs 6+
  const characterCount = Math.min(5 + Math.floor(rung / 2), 8);

  // Clues shown: start at 6, drop by 1 every 2 rungs, floor at 2
  const clueCount = Math.max(6 - Math.floor(rung / 2), 2);

  // Red herrings: 0 for first 4 rungs, then 1 per 2 rungs
  const redHerrings = Math.max(0, Math.floor((rung - 3) / 2));

  const timeLimitSeconds = rung >= 8 ? Math.max(180 - (rung - 8) * 10, 60) : null;

  return { characterCount, clueCount, redHerrings, timeLimitSeconds };
}

function buildLevel(n) {
  const isMemory   = n % 2 === 1;
  const type       = isMemory ? 'memory' : 'riddle';
  const nameList   = isMemory ? MEMORY_NAMES : RIDDLE_NAMES;
  const nameIdx    = Math.floor((n - 1) / 2);
  const name       = nameList[nameIdx] || `${isMemory ? 'Memory' : 'Riddle'} Level ${n}`;
  const difficulty = isMemory ? getMemoryDifficulty(n) : getRiddleDifficulty(n);
  const baseCoins  = 10 + Math.floor(n / 2) * 2;

  return {
    level: n,
    type,
    name,
    difficulty,
    baseCoins,
    path: n <= 2 ? `/level${n}` : `/level/${n}`,
  };
}

export const LEVEL_CONFIG   = Array.from({ length: 50 }, (_, i) => buildLevel(i + 1));
export const getLevelConfig  = (n) => LEVEL_CONFIG.find(l => l.level === n);
export const TOTAL_LEVELS    = LEVEL_CONFIG.length;